import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatMoney, CATEGORY_LABEL } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";

export default function Analytics() {
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 12);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const load = async () => {
    const params = {};
    if (start) params.start_date = new Date(start).toISOString();
    if (end) params.end_date = new Date(end + "T23:59:59").toISOString();
    const r = await api.get("/analytics/summary", { params });
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setThisYear = () => { const y = new Date().getFullYear(); setStart(`${y}-01-01`); setEnd(`${y}-12-31`); };
  const setThisMonth = () => { const d = new Date(); setStart(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`); setEnd(new Date().toISOString().slice(0,10)); };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div><div className="text-xs uppercase tracking-widest text-zinc-500">Raporlar</div><h1 className="text-3xl mt-1">Analiz</h1></div>

      <div className="bg-white p-4 border border-zinc-200 rounded-md flex flex-wrap gap-4 items-end">
        <div><Label>Başlangıç</Label><Input data-testid="analytics-start" type="date" value={start} onChange={(e)=>setStart(e.target.value)} /></div>
        <div><Label>Bitiş</Label><Input data-testid="analytics-end" type="date" value={end} onChange={(e)=>setEnd(e.target.value)} /></div>
        <Button variant="outline" onClick={setThisMonth}>Bu Ay</Button>
        <Button variant="outline" onClick={setThisYear}>Bu Yıl</Button>
        <Button data-testid="analytics-apply" onClick={load} className="bg-zinc-900 hover:bg-zinc-800">Uygula</Button>
      </div>

      {!data ? <div className="text-zinc-500">Yükleniyor...</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-5 border border-zinc-200 shadow-none">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Satış Toplamı</div>
              <div className="text-2xl number mt-2 pos">{formatMoney(data.sales_total)}</div>
              <div className="text-xs text-zinc-500 mt-1">{data.sales_count} fatura</div>
            </Card>
            <Card className="p-5 border border-zinc-200 shadow-none">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Alış Toplamı</div>
              <div className="text-2xl number mt-2 neg">{formatMoney(data.purchases_total)}</div>
              <div className="text-xs text-zinc-500 mt-1">{data.purchases_count} fatura</div>
            </Card>
            <Card className="p-5 border border-zinc-200 shadow-none">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Çalışan Ödemeleri</div>
              <div className="text-2xl number mt-2">{formatMoney(data.employee_payments)}</div>
            </Card>
            <Card className="p-5 border border-zinc-200 shadow-none">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Net</div>
              <div className={`text-2xl number mt-2 ${(data.sales_total-data.purchases_total)>=0?"pos":"neg"}`}>{formatMoney(data.sales_total - data.purchases_total)}</div>
            </Card>
          </div>

          <div className="bg-white p-5 border border-zinc-200 rounded-md">
            <div className="text-sm font-medium mb-3">Aylık Satış Trendi</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.monthly_sales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" tick={{fontSize: 12}} />
                <YAxis tick={{fontSize: 12}} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Bar dataKey="sales" fill="#27272A" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-5 border border-zinc-200 rounded-md">
              <div className="text-sm font-medium mb-3">En Çok Satan Ürünler</div>
              <table className="acc">
                <thead><tr><th>Ürün</th><th className="num">Miktar</th><th className="num">Ciro</th></tr></thead>
                <tbody>
                  {data.top_products.length===0 && <tr><td colSpan={3} className="text-zinc-500 py-4 text-center">Veri yok</td></tr>}
                  {data.top_products.map((p, i) => (
                    <tr key={i}><td>{p.name}</td><td className="num">{p.quantity}</td><td className="num">{formatMoney(p.revenue)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-white p-5 border border-zinc-200 rounded-md">
              <div className="text-sm font-medium mb-3">Kategori Bazında İşlemler</div>
              <table className="acc">
                <thead><tr><th>Kategori</th><th className="num">Adet</th><th className="num">Toplam</th></tr></thead>
                <tbody>
                  {data.transactions_by_category.length===0 && <tr><td colSpan={3} className="text-zinc-500 py-4 text-center">Veri yok</td></tr>}
                  {data.transactions_by_category.map((c, i) => (
                    <tr key={i}><td>{CATEGORY_LABEL[c.category] || c.category}</td><td className="num">{c.count}</td><td className="num">{formatMoney(c.total)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
